/**
 * SponsorBlock presentation logic: which sponsorship interval the room is
 * currently inside, and where the intervals sit on the seek bar.
 *
 * Pure by design — no DOM, no timers, no network. The client never contacts
 * SponsorBlock: the server performs one bounded lookup per media and ships the
 * merged, duration-clamped result inside `ActiveMedia.sponsor_segments`, so no
 * video ID (full or hashed) leaves the browser and every viewer sees the same
 * intervals.
 *
 * Three rules from plan/04 shape this module:
 *
 * 1. **Never automatic.** Nothing here decides to seek. Membership only
 *    decides whether a *button* is offered; the click is user intent that
 *    travels the normal command path and is re-validated server-side.
 * 2. **The authoritative position, not a local one.** Membership is tested
 *    against the room's drift-corrected expected position (phase 03's
 *    `expectedPositionMs`), not against `video.currentTime`, so two clients
 *    agree about whether a segment is current.
 * 3. **Empty is normal.** A disabled lookup, a 404, a timeout, and a rejected
 *    response all arrive as an empty list, which renders as "no segments"
 *    rather than an error.
 */

import type { PlaybackState, SponsorSegment } from '../generated';

/** Attribution required by the SponsorBlock API/data terms (plan/04 item 6). */
export const SPONSORBLOCK_ATTRIBUTION = {
  name: 'SponsorBlock',
  url: 'https://sponsor.ajay.app',
  /** Shown next to the link; the data is user-submitted, not ours. */
  note: 'Segment data is contributed by the SponsorBlock community (CC BY-NC-SA 4.0).',
} as const;

const NO_SEGMENTS: readonly SponsorSegment[] = [];

/**
 * The current media's sponsorship intervals, or an empty list whenever no
 * media is attached. Read from the snapshot rather than cached in the player:
 * the server's lookup finishes asynchronously and arrives as a later snapshot
 * for the *same* media generation.
 */
export function sponsorSegmentsFor(playback: PlaybackState): readonly SponsorSegment[] {
  switch (playback.state) {
    case 'playing':
    case 'paused':
    case 'buffering':
      return playback.media.sponsor_segments;
    case 'idle':
    case 'preparing':
    case 'failed':
      return NO_SEGMENTS;
  }
}

/**
 * The segment containing `positionMs`, or `null`. Half-open `[start, end)`, so
 * the instant a skip lands on `end_ms` the button disappears instead of
 * offering a second jump — the same boundary the server re-checks, which keeps
 * a stale click a no-op rather than a jump through the next segment.
 *
 * `positionMs` is `null` before the first clock estimate; no position means no
 * membership claim.
 */
export function activeSponsorSegment(
  segments: readonly SponsorSegment[],
  positionMs: number | null,
): SponsorSegment | null {
  if (positionMs === null) return null;
  for (const segment of segments) {
    if (positionMs >= segment.start_ms && positionMs < segment.end_ms) return segment;
  }
  return null;
}

/** One sponsorship interval placed on the seek bar, in percent of duration. */
export interface SponsorMarker {
  readonly key: string;
  /** Distance from the bar's left edge, 0–100. */
  readonly startPercent: number;
  /** Width, clamped so a very short segment stays visible (and ≤ remaining bar). */
  readonly widthPercent: number;
}

/** Minimum rendered width so a few-second segment is still a visible mark. */
const MIN_MARKER_PERCENT = 0.6;

/**
 * Marker geometry for the seek bar. Segments outside the known duration are
 * dropped rather than clamped to the end, where they would claim a range the
 * media does not have; an unknown duration (0) yields no markers at all.
 */
export function sponsorMarkers(
  segments: readonly SponsorSegment[],
  durationMs: number,
): readonly SponsorMarker[] {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return [];
  const markers: SponsorMarker[] = [];
  for (const segment of segments) {
    if (segment.end_ms <= segment.start_ms) continue;
    if (segment.start_ms >= durationMs) continue;
    const startPercent = (Math.max(segment.start_ms, 0) / durationMs) * 100;
    const endPercent = (Math.min(segment.end_ms, durationMs) / durationMs) * 100;
    markers.push({
      key: segment.key,
      startPercent,
      widthPercent: Math.min(
        Math.max(endPercent - startPercent, MIN_MARKER_PERCENT),
        100 - startPercent,
      ),
    });
  }
  return markers;
}
